import { Component, Input } from '@angular/core';

export type CreditsLevel = 'ok' | 'warn' | 'low' | 'unlimited';

/**
 * Circular percentage of AI credits left in the current period
 * (chamba-ai#433) - lives in the navbar so it's visible on every page,
 * replacing the dashboard's "AI credits left" stat card.
 *
 * `total === null` means an unlimited plan (same contract as
 * UserService.getMyCredits()).
 */
@Component({
  selector: 'app-credits-ring',
  standalone: true,
  templateUrl: './credits-ring.component.html',
  styleUrls: ['./credits-ring.component.css'],
})
export class CreditsRingComponent {
  @Input() remaining: number | null = null;
  @Input() total: number | null = null;
  /** Diameter in px. */
  @Input() size = 36;

  readonly stroke = 3.5;

  get unlimited(): boolean {
    return this.total === null;
  }

  /** 0-100, rounded. Unlimited plans read as a full ring. */
  get percent(): number {
    if (this.unlimited) return 100;
    if (!this.total || this.total <= 0) return 0;
    const ratio = (this.remaining ?? 0) / this.total;
    return Math.round(Math.min(1, Math.max(0, ratio)) * 100);
  }

  get level(): CreditsLevel {
    if (this.unlimited) return 'unlimited';
    if (this.percent <= 15) return 'low';
    if (this.percent <= 40) return 'warn';
    return 'ok';
  }

  get radius(): number {
    return (this.size - this.stroke) / 2;
  }

  get circumference(): number {
    return 2 * Math.PI * this.radius;
  }

  get dashOffset(): number {
    return this.circumference * (1 - this.percent / 100);
  }
}
